import { IVisitSalesItemTraitListingModel, VisitSalesItemTraitListingModel } from "@common/models/VisitSalesItemTraitListingModel";
import { CommunicationType } from "./Enums/CommunicationType";
import { IAddClientEmailRequest } from "./requests";
import { IStorageContentModel, StorageContentModel } from "./StorageContentModel";
import { IVisitAttachmentModel, VisitAttachmentModel } from "./VisitAttachmentModel";
import { IVisitSalesItemAttributeModel, VisitSalesItemAttributeModel } from "./VisitSalesItemAttributeModel";

export interface IMedicalHistoryModel {
    row: number;
    sortOrder: number;
    petId: number;
    visitId: number;
    clinicId: number;
    clientId: number;
    visitSalesItemId: number | undefined;
    visitSalesItemOptionId: number | undefined;
    salesItemId: number | undefined;
    salesPackageId: number | undefined;
    salesDate: string;
    code: string;
    description: string | undefined;
    name: string;
    fullName: string;
    price: number | undefined;
    invoicePrice: number | undefined;
    quantity: number | undefined;
    storeCode: string;
    hospitalName: string;
    veterinarianName: string;
    veterinarianLicense: string;
    isRabiesVaccination: boolean;
    isPending: boolean;
    given: boolean;
    communicationTypeId: number | undefined;
    communicationTypeName: string | undefined;
    diagnosticResultsDocumentId: number | undefined;
    diagnosticResultsBatchGroup: string | undefined;
    diagnosticResultsInterpreted: string | undefined;
    diagnosticResultsRawData: string | undefined;
    supplierProductCode: string | undefined;
    supplierProductName: string | undefined;
    creator: string;
    optionName: string | undefined;
    optionDisplayName: string | undefined;
    hasRabiesDoc: boolean;
    hasVisitSummaryDoc: boolean;
    hasPrescriptionDoc: boolean;
    hasInstantTestResults: boolean;
    hasDiagnosticTestResults: boolean;
    visitCompletionTypeId: number;
    isComplete: boolean;
    isFinalized: boolean;
    isPaid: boolean;
    isDiagnosis: boolean;
    salesItemSalesItemTypeId: number | undefined;
    salesItemDiscountTypeId: number | undefined;
    salesItemReminderDays: number | undefined;
    visitDiscountAmount: number;
    prescriptionId: number | undefined;
    prescriptionPublicId: string | undefined;
    prescriptionStatusId: number | undefined;
    prescriptionStatusName: string | undefined;
    prescriptionStatusCanBeRefilledByPetPass: boolean | undefined;
    prescriptionUnAbleToRefillMessage: string | undefined;
    prescriptionRefillSalesItemOptionId: number | undefined;
    diagnosticResultsDocument: IStorageContentModel | undefined;
    labReqForm?: IStorageContentModel | undefined;
    visitSalesItemAttributes: IVisitSalesItemAttributeModel[];
    visitSalesItemTraits: IVisitSalesItemTraitListingModel[];
    isDeclined: boolean;
    labOrderId: number | undefined;
    labOrderPublicId: string | undefined;
    labOrderDiagnosticResultsDocumentId: number | undefined;
    labOrderDiagnosticResultsDocument: IStorageContentModel | undefined;
    visitAttachments: IVisitAttachmentModel[];

    appointmentType: string | undefined;
    appointmentColor: string | undefined;

    // UI props
    isNote: boolean | undefined;
    businessLineId: number;
}

export class MedicalHistoryModel implements Record<keyof IMedicalHistoryModel, any> {
    row: number;
    sortOrder: number;
    petId: number;
    visitId: number;
    clinicId: number;
    clientId: number;
    visitSalesItemId: number | undefined;
    visitSalesItemOptionId: number | undefined;
    salesItemId: number | undefined;
    salesPackageId: number | undefined;
    salesDate: string;
    code: string;
    description: string | undefined;
    name: string;
    fullName: string;
    price: number | undefined;
    invoicePrice: number | undefined;
    quantity: number | undefined;
    storeCode: string;
    hospitalName: string;
    veterinarianName: string;
    veterinarianLicense: string;
    isRabiesVaccination: boolean;
    isPending: boolean;
    given: boolean;
    communicationTypeId: number | undefined;
    communicationTypeName: string | undefined;
    diagnosticResultsDocumentId: number | undefined;
    diagnosticResultsBatchGroup: string | undefined;
    diagnosticResultsInterpreted: string | undefined;
    diagnosticResultsRawData: string | undefined;
    supplierProductCode: string | undefined;
    supplierProductName: string | undefined;
    creator: string;
    optionName: string | undefined;
    optionDisplayName: string | undefined;
    hasRabiesDoc: boolean;
    hasVisitSummaryDoc: boolean;
    hasPrescriptionDoc: boolean;
    hasInstantTestResults: boolean;
    hasDiagnosticTestResults: boolean;
    visitCompletionTypeId: number;
    isComplete: boolean;
    isFinalized: boolean;
    isPaid: boolean;
    isDiagnosis: boolean;
    salesItemSalesItemTypeId: number | undefined;
    salesItemDiscountTypeId: number | undefined;
    salesItemReminderDays: number | undefined;
    visitDiscountAmount: number;
    prescriptionId: number | undefined;
    prescriptionPublicId: string | undefined;
    prescriptionStatusId: number | undefined;
    prescriptionStatusName: string | undefined;
    prescriptionStatusCanBeRefilledByPetPass: boolean | undefined;
    prescriptionUnAbleToRefillMessage: string | undefined;
    prescriptionRefillSalesItemOptionId: number | undefined;
    diagnosticResultsDocument: IStorageContentModel | undefined;
    labReqForm: IStorageContentModel | undefined;
    visitSalesItemAttributes: VisitSalesItemAttributeModel[];
    visitSalesItemTraits: IVisitSalesItemTraitListingModel[];
    isDeclined: boolean;
    labOrderId: number | undefined;
    labOrderPublicId: string | undefined;
    labOrderDiagnosticResultsDocumentId: number | undefined;
    labOrderDiagnosticResultsDocument: IStorageContentModel | undefined;
    visitAttachments: VisitAttachmentModel[];
    appointmentType: string | undefined;
    appointmentColor: string | undefined;
    businessLineId: number;

    // UI props
    attributeElements: (string | JSX.Element | JSX.Element[])[];
    isNote: boolean | undefined;

    constructor(params: Partial<IMedicalHistoryModel> = {}) {
        const {
            row = 0,
            sortOrder = 0,
            petId = 0,
            visitId = 0,
            clinicId = 0,
            clientId = 0,
            visitSalesItemId = undefined,
            visitSalesItemOptionId = undefined,
            salesItemId = 0,
            salesPackageId = 0,
            salesDate = "",
            code = "",
            description = "",
            name = "",
            fullName = "",
            price = undefined,
            invoicePrice = undefined,
            quantity = undefined,
            storeCode = "",
            hospitalName = "",
            veterinarianName = "",
            veterinarianLicense = "",
            isRabiesVaccination = false,
            isPending = false,
            given = false,
            communicationTypeId = undefined,
            communicationTypeName = "",
            diagnosticResultsDocumentId = undefined,
            diagnosticResultsBatchGroup = "",
            diagnosticResultsInterpreted = "",
            diagnosticResultsRawData = "",
            supplierProductCode = "",
            supplierProductName = "",
            creator = "",
            optionName = "",
            optionDisplayName = "",
            hasRabiesDoc = false,
            hasVisitSummaryDoc = false,
            hasPrescriptionDoc = false,
            hasInstantTestResults = false,
            hasDiagnosticTestResults = false,
            visitCompletionTypeId = 0,
            isComplete = false,
            isFinalized = false,
            isPaid = false,
            isDiagnosis = false,
            salesItemSalesItemTypeId = undefined,
            salesItemDiscountTypeId = undefined,
            salesItemReminderDays = undefined,
            visitDiscountAmount = 0,
            prescriptionId = undefined,
            prescriptionPublicId = undefined,
            prescriptionStatusId = undefined,
            prescriptionStatusName = "",
            prescriptionStatusCanBeRefilledByPetPass = undefined,
            prescriptionUnAbleToRefillMessage = "",
            prescriptionRefillSalesItemOptionId = undefined,
            diagnosticResultsDocument = undefined,
            visitSalesItemAttributes = [],
            visitSalesItemTraits = [],
            isDeclined = false,
            labOrderId = undefined,
            labOrderPublicId = undefined,
            labOrderDiagnosticResultsDocumentId = undefined,
            labOrderDiagnosticResultsDocument = undefined,
            labReqForm = undefined,
            visitAttachments: _visitAttachments = [],
            businessLineId = 0,
        } = params;

        this.row = row;
        this.sortOrder = sortOrder;
        this.petId = petId;
        this.visitId = visitId;
        this.clinicId = clinicId;
        this.clientId = clientId;
        this.visitSalesItemId = visitSalesItemId;
        this.visitSalesItemOptionId = visitSalesItemOptionId;
        this.salesItemId = salesItemId;
        this.salesPackageId = salesPackageId;
        this.salesDate = salesDate;
        this.code = code;
        this.description = description;
        this.name = name;
        this.fullName = fullName;
        this.price = price;
        this.invoicePrice = invoicePrice;
        this.quantity = quantity;
        this.storeCode = storeCode;
        this.hospitalName = hospitalName;
        this.veterinarianName = veterinarianName;
        this.veterinarianLicense = veterinarianLicense;
        this.isRabiesVaccination = isRabiesVaccination;
        this.isPending = isPending;
        this.given = given;
        this.communicationTypeId = communicationTypeId;
        this.communicationTypeName = communicationTypeName;
        this.diagnosticResultsDocumentId = diagnosticResultsDocumentId;
        this.diagnosticResultsBatchGroup = diagnosticResultsBatchGroup;
        this.diagnosticResultsInterpreted = diagnosticResultsInterpreted;
        this.diagnosticResultsRawData = diagnosticResultsRawData;
        this.supplierProductCode = supplierProductCode;
        this.supplierProductName = supplierProductName;
        this.creator = creator;
        this.optionName = optionName;
        this.optionDisplayName = optionDisplayName;
        this.hasRabiesDoc = hasRabiesDoc;
        this.hasVisitSummaryDoc = hasVisitSummaryDoc;
        this.hasPrescriptionDoc = hasPrescriptionDoc;
        this.hasInstantTestResults = hasInstantTestResults;
        this.hasDiagnosticTestResults = hasDiagnosticTestResults;
        this.visitCompletionTypeId = visitCompletionTypeId;
        this.isComplete = isComplete;
        this.isFinalized = isFinalized;
        this.isPaid = isPaid;
        this.isDiagnosis = isDiagnosis;
        this.salesItemSalesItemTypeId = salesItemSalesItemTypeId;
        this.salesItemDiscountTypeId = salesItemDiscountTypeId;
        this.salesItemReminderDays = salesItemReminderDays;
        this.visitDiscountAmount = visitDiscountAmount;
        this.prescriptionId = prescriptionId;
        this.prescriptionPublicId = prescriptionPublicId;
        this.prescriptionStatusId = prescriptionStatusId;
        this.prescriptionStatusName = prescriptionStatusName;
        this.prescriptionStatusCanBeRefilledByPetPass = prescriptionStatusCanBeRefilledByPetPass;
        this.prescriptionUnAbleToRefillMessage = prescriptionUnAbleToRefillMessage;
        this.prescriptionRefillSalesItemOptionId = prescriptionRefillSalesItemOptionId;
        this.diagnosticResultsDocument = diagnosticResultsDocument !== undefined ? new StorageContentModel(diagnosticResultsDocument) : diagnosticResultsDocument;
        this.visitSalesItemAttributes = (visitSalesItemAttributes ?? []).map(attr => new VisitSalesItemAttributeModel(attr));
        this.visitSalesItemTraits = (visitSalesItemTraits ?? []).map(t => new VisitSalesItemTraitListingModel(t));
        this.isDeclined = isDeclined;
        this.labOrderId = labOrderId;
        this.labOrderPublicId = labOrderPublicId;
        this.labOrderDiagnosticResultsDocumentId = labOrderDiagnosticResultsDocumentId;
        this.labOrderDiagnosticResultsDocument = labOrderDiagnosticResultsDocument !== undefined ? new StorageContentModel(labOrderDiagnosticResultsDocument) : labOrderDiagnosticResultsDocument;
        this.labReqForm = labReqForm !== undefined ? new StorageContentModel(labReqForm) : labReqForm;
        this.attributeElements = VisitSalesItemAttributeModel.parseAttributeElements(this.visitSalesItemAttributes);
        this.visitAttachments = (params.visitAttachments ?? []).map(v => new VisitAttachmentModel(v));
        this.isNote = params.isNote;
        this.appointmentType = params.appointmentType ?? "";
        this.appointmentColor = params.appointmentColor ?? "";
        this.businessLineId = businessLineId;
    }

    toClientEmailRequest(emailAddress: string, communicationTypeId: CommunicationType): IAddClientEmailRequest {
        const storageContentUrl = communicationTypeId === CommunicationType.LabResult
            ? this.diagnosticResultsDocument?.storageContentUrl ?? this.labOrderDiagnosticResultsDocument?.storageContentUrl
            : undefined;
        const includeRabiesCertificate = communicationTypeId === CommunicationType.VisitCompletionSummary

        return {
            visitId: this.visitId,
            clientId: this.clientId,
            visitSalesItemId: this.visitSalesItemId,
            petId: this.petId,
            invoiceId: 0,
            emailAddress,
            salesDate: this.salesDate,
            communicationTypeId,
            communicationTypeName: CommunicationType.displayName(communicationTypeId),
            storageContentUrl,
            includeRabiesCertificate
        }
    }
}
